import React, { useState } from 'react';
import { FileCode, Loader2, ArrowRight, AlertCircle, EyeIcon, EyeOffIcon, KeyIcon, MailIcon, LockIcon, ArrowLeftIcon, ShieldCheckIcon, PhoneIcon } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { toast } from 'sonner';
type LoginStep = 'email' | 'password' | 'set-password' | 'forgot' | 'reset-code' | 'quick-pin' | 'not-found';
export function Login() {
  const {
    identifyUser,
    verifyUserPassword,
    setupUserPassword,
    verifyQuickPin,
    requestPasswordReset,
    resetPasswordWithToken,
    loginState,
    getAdminContacts
  } = useWorkspace();
  const [step, setStep] = useState<LoginStep>(loginState?.needsPin ? 'quick-pin' : 'email');
  const [email, setEmail] = useState(loginState?.email || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pin, setPin] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resetEmail, setResetEmail] = useState('');
  const [resetRequested, setResetRequested] = useState(false);
  const [adminContacts, setAdminContacts] = useState<{
    name: string;
    email: string;
    phone: string;
    role: string;
  }[]>([]);
  // Step 1: Check if email exists
  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const result = await identifyUser(email);
      if (result === 'not-found') {
        setAdminContacts(getAdminContacts());
        setStep('not-found');
      } else if (result === 'suspended') {
        setError('Your account has been suspended. Contact your administrator.');
      } else if (result === 'needs-password') {
        setStep('set-password');
      } else if (result === 'has-password') {
        setStep('password');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  // Step 2a: Verify password
  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const success = await verifyUserPassword(email, password);
      if (!success) {
        setError('Incorrect password. Please try again.');
      }
      // If success, context will set appState to 'ready'
    } catch (err) {
      console.error('Password verification error:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  // Step 2b: Set initial password
  const handleSetPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password || !confirmPassword) return;
    if (password.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      const success = await setupUserPassword(email, password);
      if (success) {
        toast.success('Password created successfully! You are now logged in.');
      } else {
        setError('Failed to set password. Please try again.');
      }
    } catch (err) {
      console.error('Set password error:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  // Quick PIN verification
  const handlePinSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pin) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const success = await verifyQuickPin(pin);
      if (!success) {
        setError('Incorrect PIN. Please try again.');
      }
    } catch (err) {
      console.error('PIN verification error:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  // Forgot password — request reset
  const handleForgotSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetEmail.trim()) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const success = await requestPasswordReset(resetEmail);
      if (success) {
        setResetRequested(true);
        toast.success('Reset code generated. Contact your administrator for the code.');
      } else {
        setError('No account found with this email.');
      }
    } catch (err) {
      console.error('Reset request error:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  // Reset password with code
  const handleResetSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetCode || !newPassword || !confirmNewPassword) return;
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setError('Passwords do not match.');
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      const success = await resetPasswordWithToken(resetEmail, resetCode, newPassword);
      if (success) {
        toast.success('Password reset successfully! Please log in.');
        setStep('email');
        setEmail(resetEmail);
        setPassword('');
        setResetCode('');
        setNewPassword('');
        setConfirmNewPassword('');
      } else {
        setError('Invalid or expired reset code. Please try again or request a new code.');
      }
    } catch (err) {
      console.error('Reset error:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  const renderError = () => error ? <div className="rounded-lg bg-red-50 p-3 flex items-start gap-2" data-id="element-4829" data-mp="pvygbqxhdcx0">
        <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" data-id="element-4830" data-mp="pt3v3x6n7o2j" />
        <p className="text-sm text-red-600" data-id="element-4831" data-mp="3pcemlavv5vsa">{error}</p>
      </div> : null;
  const inputClass = 'block w-full rounded-lg border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-4 py-2.5 bg-white';
  const btnClass = 'w-full flex items-center justify-center gap-2 bg-gray-900 text-white rounded-lg px-4 py-2.5 text-sm font-medium hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors';
  return <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4" data-id="element-4832" data-mp="331wgkol1fbgl">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-gray-200 p-8" data-id="element-4833" data-mp="2y31wzkf8xivf">
        {/* Quick PIN Screen */}
        {step === 'quick-pin' && <>
            <div className="flex flex-col items-center text-center mb-8" data-id="element-4834" data-mp="2u3azyyz4m6ig">
              <div className="h-12 w-12 bg-blue-50 rounded-xl flex items-center justify-center mb-4" data-id="element-4835" data-mp="2p1vi1fs8wg4a">
                <ShieldCheckIcon className="h-6 w-6 text-blue-600" data-id="element-4836" data-mp="2k4lg50no6bzk" />
              </div>
              <h1 className="text-2xl font-semibold text-gray-900" data-id="element-4837" data-mp="2fb0sa1fsiye3">
                Welcome back
              </h1>
              <p className="text-sm text-gray-500 mt-2" data-id="element-4838" data-mp="2adqx055pzsic">
                Enter your Quick PIN to continue as{' '}
                <span className="font-medium text-gray-700" data-id="element-4839" data-mp="26a2n3ftm8ktx">
                  {loginState?.email}
                </span>
              </p>
            </div>
            <form onSubmit={handlePinSubmit} className="space-y-4" data-id="element-4840" data-mp="1wf0wefjozap8">
              <div data-id="element-4841" data-mp="3k9w1dcdreuaz">
                <label htmlFor="pin" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4842" data-mp="3kbgvj0w5wb8b">
                  Quick PIN
                </label>
                <input type="password" id="pin" required className={inputClass} placeholder="Enter your PIN or phrase" value={pin} onChange={e => {
              setPin(e.target.value);
              setError(null);
            }} autoFocus data-id="element-4843" data-mp="3b8o4myqw61j0" />
              </div>
              {renderError()}
              <button type="submit" disabled={isSubmitting || !pin} className={btnClass} data-id="element-4844" data-mp="2wkjtcfu64sa9">
                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" data-id="element-4845" data-mp="2nk54x7lwqx2j" /> : <>
                    Unlock <ArrowRight className="h-4 w-4" data-id="element-4846" data-mp="23xtjm7xrhvbq" />
                  </>}
              </button>
              <div className="flex items-center justify-between text-xs" data-id="element-4847" data-mp="1z01ufloi4nb8">
                <button type="button" onClick={() => {
              setStep('password');
              setError(null);
            }} className="text-blue-600 hover:text-blue-700 font-medium" data-id="element-4848" data-mp="2dw9coggcnenv">
                  Use password instead
                </button>
                <button type="button" onClick={() => {
              setStep('email');
              setEmail('');
              setError(null);
            }} className="text-gray-500 hover:text-gray-700" data-id="element-4849" data-mp="2hxcs51uhj8ok">
                  Switch account
                </button>
              </div>
            </form>
          </>}

        {/* Email Screen */}
        {step === 'email' && <>
            <div className="flex flex-col items-center text-center mb-8" data-id="element-4850" data-mp="p3w3p1hoaw1z">
              <div className="h-12 w-12 bg-blue-50 rounded-xl flex items-center justify-center mb-4" data-id="element-4851" data-mp="kbvwcnz9x0vd">
                <FileCode className="h-6 w-6 text-blue-600" data-id="element-4852" data-mp="fcjbg8ryq3vf" />
              </div>
              <h1 className="text-2xl font-semibold text-gray-900" data-id="element-4853" data-mp="aehrllmpq0iw">Welcome</h1>
              <p className="text-sm text-gray-500 mt-2" data-id="element-4854" data-mp="acddkav3nl4w">
                Enter your email to access ConfigConsole.
              </p>
            </div>
            <form onSubmit={handleEmailSubmit} className="space-y-4" data-id="element-4855" data-mp="133x5cj5ri7ii">
              <div data-id="element-4856" data-mp="y3iuktcs0jgs">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4857" data-mp="y30xxvm7c38c">
                  Email Address
                </label>
                <div className="relative" data-id="element-4858" data-mp="3iv2s0e1a3lae">
                  <MailIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-4859" data-mp="3iroqf80fvg40" />
                  <input type="email" id="email" required className={`${inputClass} pl-10`} placeholder="you@company.com" value={email} onChange={e => {
                setEmail(e.target.value);
                setError(null);
              }} autoFocus data-id="element-4860" data-mp="3eusod80yl6yp" />
                </div>
              </div>
              {renderError()}
              <button type="submit" disabled={isSubmitting || !email.trim()} className={btnClass} data-id="element-4861" data-mp="2hzq2pb6nhloh">
                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" data-id="element-4862" data-mp="2r0ehi83vbq2f" /> : <>
                    Continue <ArrowRight className="h-4 w-4" data-id="element-4863" data-mp="roc0d4ipv7rx" />
                  </>}
              </button>
            </form>
          </>}

        {/* Password Screen */}
        {step === 'password' && <>
            <div className="flex flex-col items-center text-center mb-8" data-id="element-4864" data-mp="1zxsz58iruti5">
              <div className="h-12 w-12 bg-blue-50 rounded-xl flex items-center justify-center mb-4" data-id="element-4865" data-mp="1uzhlempcvyun">
                <LockIcon className="h-6 w-6 text-blue-600" data-id="element-4866" data-mp="28z0kkq06vzut" />
              </div>
              <h1 className="text-2xl font-semibold text-gray-900" data-id="element-4867" data-mp="23yuxln4v71j2">
                Enter password
              </h1>
              <p className="text-sm text-gray-500 mt-2" data-id="element-4868" data-mp="2iv3uwj5734su">
                Logging in as{' '}
                <span className="font-medium text-gray-700" data-id="element-4869" data-mp="2dw90ejxv7rsw">{email}</span>
              </p>
            </div>
            <form onSubmit={handlePasswordSubmit} className="space-y-4" data-id="element-4870" data-mp="2rnfbzeef5fw5">
              <div data-id="element-4871" data-mp="2nmvc432gny6b">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4872" data-mp="2noesdto4a983">
                  Password
                </label>
                <div className="relative" data-id="element-4873" data-mp="2wp4pd0p465ne">
                  <LockIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-4874" data-mp="3g9lluayvav4u" />
                  <input type={showPassword ? 'text' : 'password'} id="password" required className={`${inputClass} pl-10 pr-10`} placeholder="Enter your password" value={password} onChange={e => {
                setPassword(e.target.value);
                setError(null);
              }} autoFocus data-id="element-4875" data-mp="3l8epzcs9l9sc" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" data-id="element-4876" data-mp="2nl69xxb18sxq">
                    {showPassword ? <EyeOffIcon className="h-4 w-4" data-id="element-4877" data-mp="2iltlj84ijdt1" /> : <EyeIcon className="h-4 w-4" data-id="element-4878" data-mp="2innz21dgg3n3" />}
                  </button>
                </div>
              </div>
              {renderError()}
              <button type="submit" disabled={isSubmitting || !password} className={btnClass} data-id="element-4879" data-mp="26k258qlkqpze">
                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" data-id="element-4880" data-mp="1wsz0netfrsvd" /> : <>
                    Log In <ArrowRight className="h-4 w-4" data-id="element-4881" data-mp="1stgiurwwvxwr" />
                  </>}
              </button>
              <div className="flex items-center justify-between text-xs" data-id="element-4882" data-mp="1nv5aklm9lvgn">
                <button type="button" onClick={() => {
              setStep('forgot');
              setResetEmail(email);
              setError(null);
            }} className="text-blue-600 hover:text-blue-700 font-medium" data-id="element-4883" data-mp="1iuhj5yu4jclw">
                  Forgot password?
                </button>
                <button type="button" onClick={() => {
              setStep('email');
              setPassword('');
              setError(null);
            }} className="text-gray-500 hover:text-gray-700" data-id="element-4884" data-mp="3gov5ac9flk2c">
                  <ArrowLeftIcon className="h-3 w-3 inline mr-1" data-id="element-4885" data-mp="3ug9q8omwfvc1" />
                  Back
                </button>
              </div>
            </form>
          </>}

        {/* Set Password Screen (first time) */}
        {step === 'set-password' && <>
            <div className="flex flex-col items-center text-center mb-8" data-id="element-4886" data-mp="36r77fdgj3wfy">
              <div className="h-12 w-12 bg-green-50 rounded-xl flex items-center justify-center mb-4" data-id="element-4887" data-mp="3bqtknmwcxcor">
                <KeyIcon className="h-6 w-6 text-green-600" data-id="element-4888" data-mp="m1hu54vh4ipd" />
              </div>
              <h1 className="text-2xl font-semibold text-gray-900" data-id="element-4889" data-mp="m3lzlr90tprr">
                Create your password
              </h1>
              <p className="text-sm text-gray-500 mt-2" data-id="element-4890" data-mp="1c6x187e4ov6">
                Welcome! Set a password for{' '}
                <span className="font-medium text-gray-700" data-id="element-4891" data-mp="3sj9lvszraj28">{email}</span> to
                secure your account.
              </p>
            </div>
            <form onSubmit={handleSetPasswordSubmit} className="space-y-4" data-id="element-4892" data-mp="b9v3uwi9tmtp">
              <div data-id="element-4893" data-mp="6cl2qjlirmq1">
                <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4894" data-mp="6cbdiy8atjle">
                  Password
                </label>
                <div className="relative" data-id="element-4895" data-mp="39q5pw0z2fu4g">
                  <LockIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-4896" data-mp="3ohofmqhq9310" />
                  <input type={showPassword ? 'text' : 'password'} id="new-password" required minLength={8} className={`${inputClass} pl-10 pr-10`} placeholder="At least 8 characters" value={password} onChange={e => {
                setPassword(e.target.value);
                setError(null);
              }} autoFocus data-id="element-4897" data-mp="3jq6cpww58sde" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" data-id="element-4898" data-mp="wndhurffpdkt">
                    {showPassword ? <EyeOffIcon className="h-4 w-4" data-id="element-4899" data-mp="1u8squqi5586w" /> : <EyeIcon className="h-4 w-4" data-id="element-4900" data-mp="1g8rmv26kxkn7" />}
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-1" data-id="element-4901" data-mp="1k9blka6swwbg">
                  Minimum 8 characters
                </p>
              </div>
              <div data-id="element-4902" data-mp="8vpxkscoh0ri">
                <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4903" data-mp="8w80g7tuid8e">
                  Confirm Password
                </label>
                <div className="relative" data-id="element-4904" data-mp="vjogzv05shac">
                  <LockIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-4905" data-mp="riurwyazcwfv" />
                  <input type={showConfirmPassword ? 'text' : 'password'} id="confirm-password" required minLength={8} className={`${inputClass} pl-10 pr-10`} placeholder="Re-enter your password" value={confirmPassword} onChange={e => {
                setConfirmPassword(e.target.value);
                setError(null);
              }} data-id="element-4906" data-mp="mjrnng2tqu8y" />
                  <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" data-id="element-4907" data-mp="22z2x77hsqlv8">
                    {showConfirmPassword ? <EyeOffIcon className="h-4 w-4" data-id="element-4908" data-mp="2ai2g7c1cibe2" /> : <EyeIcon className="h-4 w-4" data-id="element-4909" data-mp="2ffke87dtr67a" />}
                  </button>
                </div>
              </div>
              {renderError()}
              <button type="submit" disabled={isSubmitting || !password || !confirmPassword} className={btnClass} data-id="element-4910" data-mp="2ohl9wztetk6u">
                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" data-id="element-4911" data-mp="2y6v3kk3wjax2" /> : <>
                    Create Password & Log In <ArrowRight className="h-4 w-4" data-id="element-4912" data-mp="335okkmiedk1z" />
                  </>}
              </button>
              <button type="button" onClick={() => {
            setStep('email');
            setPassword('');
            setConfirmPassword('');
            setError(null);
          }} className="w-full text-center text-xs text-gray-500 hover:text-gray-700" data-id="element-4913" data-mp="379mfsm4lqapr">
                <ArrowLeftIcon className="h-3 w-3 inline mr-1" data-id="element-4914" data-mp="pyiz3knzx57b" />
                Back to email
              </button>
            </form>
          </>}

        {/* Forgot Password Screen */}
        {step === 'forgot' && <>
            <div className="flex flex-col items-center text-center mb-8" data-id="element-4915" data-mp="zwq558gqy2uj">
              <div className="h-12 w-12 bg-amber-50 rounded-xl flex items-center justify-center mb-4" data-id="element-4916" data-mp="14urv8dclb2fi">
                <KeyIcon className="h-6 w-6 text-amber-600" data-id="element-4917" data-mp="19o2x002jot0k" />
              </div>
              <h1 className="text-2xl font-semibold text-gray-900" data-id="element-4918" data-mp="1dnbmutugs6qu">
                Reset password
              </h1>
              <p className="text-sm text-gray-500 mt-2" data-id="element-4919" data-mp="1ip10mh92myim">
                Enter your email to receive a reset code from your
                administrator.
              </p>
            </div>
            {!resetRequested ? <form onSubmit={handleForgotSubmit} className="space-y-4" data-id="element-4920" data-mp="1xoeme8oja8v3">
                <div data-id="element-4921" data-mp="1sptkm4tmvh4j">
                  <label htmlFor="reset-email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4922" data-mp="1sthh9cudkjnh">
                    Email Address
                  </label>
                  <input type="email" id="reset-email" required className={inputClass} placeholder="you@company.com" value={resetEmail} onChange={e => {
              setResetEmail(e.target.value);
              setError(null);
            }} autoFocus data-id="element-4923" data-mp="2ggpk7o4bzgmc" />
                </div>
                {renderError()}
                <button type="submit" disabled={isSubmitting || !resetEmail.trim()} className={btnClass} data-id="element-4924" data-mp="2ug8fvhfygzhr">
                  {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" data-id="element-4925" data-mp="xikodmqxsc66" /> : <>
                      Request Reset Code <ArrowRight className="h-4 w-4" data-id="element-4926" data-mp="it4wg3fo60ow" />
                    </>}
                </button>
                <button type="button" onClick={() => {
            setStep('email');
            setError(null);
          }} className="w-full text-center text-xs text-gray-500 hover:text-gray-700" data-id="element-4927" data-mp="nq52wfgfl5gj">
                  <ArrowLeftIcon className="h-3 w-3 inline mr-1" data-id="element-4928" data-mp="3w216wriownmo" />
                  Back to login
                </button>
              </form> : <div className="space-y-4" data-id="element-4929" data-mp="1u9v8i36rxmqf">
                <div className="bg-blue-50 rounded-lg p-4 text-sm text-blue-800" data-id="element-4930" data-mp="1u6qy2o2pturb">
                  <p className="font-medium mb-1" data-id="element-4931" data-mp="1z6vbu6c8obdu">Reset code generated</p>
                  <p data-id="element-4932" data-mp="3jqh0c36tvh8r">
                    Contact your team administrator to get your reset code. They
                    can find it in the Users management page.
                  </p>
                </div>
                <button type="button" onClick={() => setStep('reset-code')} className={btnClass} data-id="element-4933" data-mp="3th3avizothkl">
                  I have my code <ArrowRight className="h-4 w-4" data-id="element-4934" data-mp="69rdz58l49jn" />
                </button>
                <button type="button" onClick={() => {
            setStep('email');
            setResetRequested(false);
            setError(null);
          }} className="w-full text-center text-xs text-gray-500 hover:text-gray-700" data-id="element-4935" data-mp="29qtb2uw7245">
                  <ArrowLeftIcon className="h-3 w-3 inline mr-1" data-id="element-4936" data-mp="2i0ve5y6xt0gu" />
                  Back to login
                </button>
              </div>}
          </>}

        {/* Reset Code Screen */}
        {step === 'reset-code' && <>
            <div className="flex flex-col items-center text-center mb-8" data-id="element-4937" data-mp="2jgpq7fl2lt9g">
              <div className="h-12 w-12 bg-amber-50 rounded-xl flex items-center justify-center mb-4" data-id="element-4938" data-mp="2oglsnae4dcmw">
                <KeyIcon className="h-6 w-6 text-amber-600" data-id="element-4939" data-mp="2tfexyfj95xbz" />
              </div>
              <h1 className="text-2xl font-semibold text-gray-900" data-id="element-4940" data-mp="2tdcb24deckbh">
                Enter reset code
              </h1>
              <p className="text-sm text-gray-500 mt-2" data-id="element-4941" data-mp="20qrhqydd1y5a">
                Enter the code from your administrator and set a new password.
              </p>
            </div>
            <form onSubmit={handleResetSubmit} className="space-y-4" data-id="element-4942" data-mp="25o15m9l3q1r0">
              <div data-id="element-4943" data-mp="2ahm380is8tpo">
                <label htmlFor="reset-code" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4944" data-mp="2aix9q83m4fkh">
                  Reset Code
                </label>
                <input type="text" id="reset-code" required className={`${inputClass} font-mono tracking-wider`} placeholder="Enter code" value={resetCode} onChange={e => {
              setResetCode(e.target.value);
              setError(null);
            }} autoFocus data-id="element-4945" data-mp="1hyf9jv0102n8" />
              </div>
              <div data-id="element-4946" data-mp="3cdi4ikm31ri4">
                <label htmlFor="new-pw" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4947" data-mp="3ce1mu2fnahwo">
                  New Password
                </label>
                <input type="password" id="new-pw" required minLength={8} className={inputClass} placeholder="At least 8 characters" value={newPassword} onChange={e => {
              setNewPassword(e.target.value);
              setError(null);
            }} data-id="element-4948" data-mp="33h95h720ouwi" />
              </div>
              <div data-id="element-4949" data-mp="2jymrl4g2blfz">
                <label htmlFor="confirm-new-pw" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4950" data-mp="2jz4w0ocvp5yz">
                  Confirm New Password
                </label>
                <input type="password" id="confirm-new-pw" required minLength={8} className={inputClass} placeholder="Re-enter new password" value={confirmNewPassword} onChange={e => {
              setConfirmNewPassword(e.target.value);
              setError(null);
            }} data-id="element-4951" data-mp="2a1gyjqnw1kd4" />
              </div>
              {renderError()}
              <button type="submit" disabled={isSubmitting || !resetCode || !newPassword || !confirmNewPassword} className={btnClass} data-id="element-4952" data-mp="3lesz9socpmv2">
                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" data-id="element-4953" data-mp="1jpwugquvsu11" /> : <>
                    Reset Password <ArrowRight className="h-4 w-4" data-id="element-4954" data-mp="1ervdcbh0n4s7" />
                  </>}
              </button>
              <button type="button" onClick={() => {
            setStep('forgot');
            setError(null);
          }} className="w-full text-center text-xs text-gray-500 hover:text-gray-700" data-id="element-4955" data-mp="r1815e02hszu">
                <ArrowLeftIcon className="h-3 w-3 inline mr-1" data-id="element-4956" data-mp="vz9jfwpo5oa9" />
                Back
              </button>
            </form>
          </>}

        {/* Not Found Screen */}
        {step === 'not-found' && <>
            <div className="flex flex-col items-center text-center mb-8" data-id="element-4957" data-mp="27hbyco2b3jee">
              <div className="h-12 w-12 bg-red-50 rounded-xl flex items-center justify-center mb-4" data-id="element-4958" data-mp="4u5a7uix0hpk">
                <AlertCircle className="h-6 w-6 text-red-600" data-id="element-4959" data-mp="9s6rc9ws66ye" />
              </div>
              <h1 className="text-2xl font-semibold text-gray-900" data-id="element-4960" data-mp="epp285ucyw91">
                Account not found
              </h1>
              <p className="text-sm text-gray-500 mt-2" data-id="element-4961" data-mp="jirp5v4mr7g9">
                We couldn't find an account for{' '}
                <span className="font-medium text-gray-700" data-id="element-4962" data-mp="oimgu0w8c12d">{email}</span>.
              </p>
            </div>

            <div className="space-y-6" data-id="element-4963" data-mp="sjzo8fez0cbe">
              <div data-id="element-4964" data-mp="xgyg0rb5w76f">
                <h2 className="text-sm font-medium text-gray-900 mb-3" data-id="element-4965" data-mp="xij726y9p7zj">
                  Contact an administrator to get access:
                </h2>
                {adminContacts.length > 0 ? <div className="space-y-3 max-h-60 overflow-y-auto pr-2" data-id="element-4966" data-mp="2z9waerkl77th">
                    {adminContacts.map((admin, idx) => <div key={idx} className="bg-gray-50 rounded-lg p-3 border border-gray-100" data-id="element-4967" data-mp="349ipl6jz6y4x" data-mpi={idx}>
                        <div className="flex justify-between items-start mb-2" data-id="element-4968" data-mp="2rvvk18ozgoa5">
                          <span className="font-medium text-sm text-gray-900" data-id="element-4969" data-mp="2mzeqfyz0lhpo">
                            {admin.name}
                          </span>
                          <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 uppercase tracking-wider" data-id="element-4970" data-mp="3fn0v7ajzase4">
                            {admin.role}
                          </span>
                        </div>
                        <div className="text-sm text-gray-500 space-y-1.5" data-id="element-4971" data-mp="35njsn200wumd">
                          <div className="flex items-center gap-2" data-id="element-4972" data-mp="30q9xr6tv7qr5">
                            <MailIcon className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" data-id="element-4973" data-mp="30s4jgncynmwa" />
                            <a href={`mailto:${admin.email}`} className="hover:text-blue-600 transition-colors truncate" data-id="element-4974" data-mp="1u7bmkqldv15y">
                              {admin.email}
                            </a>
                          </div>
                          {admin.phone && <div className="flex items-center gap-2" data-id="element-4975" data-mp="x9jtrmp1m369">
                              <PhoneIcon className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" data-id="element-4976" data-mp="shid7okudqaf" />
                              <a href={`tel:${admin.phone}`} className="hover:text-blue-600 transition-colors truncate" data-id="element-4977" data-mp="168x3mgh2wtri">
                                {admin.phone}
                              </a>
                            </div>}
                        </div>
                      </div>)}
                  </div> : <div className="bg-gray-50 rounded-lg p-4 text-center text-sm text-gray-500 border border-gray-100" data-id="element-4978" data-mp="3czwbmm55v1in">
                    No administrators found. Please contact your IT department.
                  </div>}
              </div>

              <button type="button" onClick={() => {
            setStep('email');
            setError(null);
          }} className={btnClass} data-id="element-4979" data-mp="1xgf8otqc7gkt">
                <ArrowLeftIcon className="h-4 w-4 mr-2" data-id="element-4980" data-mp="1nosm618jtv2r" />
                Try a different email
              </button>
            </div>
          </>}

        <p className="text-center text-xs text-gray-400 mt-8" data-id="element-4981" data-mp="1dtqy78q0ez1r">
          Protected by ConfigConsole Identity
        </p>
      </div>
    </div>;
}